import { ref, computed, type Ref } from 'vue'

export interface PaginationOptions {
  initialPage?: number
  initialItemsPerPage?: number
}

export interface PaginationState {
  page: Ref<number>
  itemsPerPage: Ref<number>
  total: Ref<number>
  totalPages: Readonly<Ref<number>>
  offset: Readonly<Ref<number>>
  hasNextPage: Readonly<Ref<boolean>>
  hasPrevPage: Readonly<Ref<boolean>>
  goToPage: (page: number) => void
  nextPage: () => void
  prevPage: () => void
  setItemsPerPage: (value: number) => void
  setTotal: (value: number) => void
  buildQueryParams: () => { limit: number; offset: number }
}

export function usePagination(options: PaginationOptions = {}): PaginationState {
  const { initialPage = 1, initialItemsPerPage = 50 } = options

  const page = ref(initialPage)
  const itemsPerPage = ref(initialItemsPerPage)
  const total = ref(0)

  const totalPages = computed(() => Math.max(1, Math.ceil(total.value / itemsPerPage.value)))

  const offset = computed(() => (page.value - 1) * itemsPerPage.value)

  const hasNextPage = computed(() => page.value < totalPages.value)
  const hasPrevPage = computed(() => page.value > 1)

  const goToPage = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages.value) {
      page.value = newPage
    }
  }

  const nextPage = () => {
    if (hasNextPage.value) {
      page.value++
    }
  }

  const prevPage = () => {
    if (hasPrevPage.value) {
      page.value--
    }
  }

  const setItemsPerPage = (value: number) => {
    itemsPerPage.value = value
    page.value = 1
  }

  const setTotal = (value: number) => {
    total.value = value
    if (page.value > totalPages.value) {
      page.value = Math.max(1, totalPages.value)
    }
  }

  const buildQueryParams = () => ({
    limit: itemsPerPage.value,
    offset: offset.value
  })

  return {
    page,
    itemsPerPage,
    total,
    totalPages,
    offset,
    hasNextPage,
    hasPrevPage,
    goToPage,
    nextPage,
    prevPage,
    setItemsPerPage,
    setTotal,
    buildQueryParams
  }
}
